import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import type { AnyExtension } from '@tiptap/core'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/react'
import { editorHtmlToInline, isEmptyEditorHtml } from '@/lib/landing-html'
import type { CampaignRichTextFieldProps } from './types'
import styles from './CampaignRichTextField.module.css'

function isSafeHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

function serializeEditorHtml(html: string, variant: 'inline' | 'block'): string {
  if (isEmptyEditorHtml(html)) {
    return ''
  }

  return variant === 'inline' ? editorHtmlToInline(html) : html
}

export function CampaignRichTextField({
  id,
  value,
  onChange,
  variant,
  placeholder,
  'aria-label': ariaLabel,
  'aria-required': ariaRequired,
  'aria-describedby': ariaDescribedBy,
  minHeight,
}: CampaignRichTextFieldProps) {
  const [focused, setFocused] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('https://')
  const onChangeRef = useRef(onChange)
  const variantRef = useRef(variant)
  useLayoutEffect(() => {
    onChangeRef.current = onChange
    variantRef.current = variant
  }, [onChange, variant])

  const extensions = useMemo(() => {
    const starter = StarterKit.configure({
      heading: variant === 'block' ? { levels: [2, 3, 4] } : false,
      bulletList: variant === 'block' ? {} : false,
      orderedList: variant === 'block' ? {} : false,
      listItem: variant === 'block' ? {} : false,
      blockquote: false,
      codeBlock: false,
      code: false,
      horizontalRule: false,
      link:
        variant === 'block'
          ? {
              openOnClick: false,
              autolink: false,
              validate: isSafeHttpUrl,
              HTMLAttributes: {
                rel: 'noopener noreferrer',
                target: '_blank',
              },
            }
          : false,
    })

    const items: AnyExtension[] = [
      starter,
      Placeholder.configure({
        placeholder: placeholder ?? '',
      }),
    ]

    return items
  }, [placeholder, variant])

  const editor = useEditor({
    extensions,
    content: value || '',
    immediatelyRender: false,
    editorProps: {
      attributes: {
        id,
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': ariaLabel ?? placeholder ?? 'Editor de texto',
        'aria-required': ariaRequired ? 'true' : 'false',
        ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
      },
    },
    onUpdate: ({ editor: current }) => {
      onChangeRef.current(serializeEditorHtml(current.getHTML(), variantRef.current))
    },
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  })

  useEffect(() => {
    if (!editor) return

    const current = serializeEditorHtml(editor.getHTML(), variant)
    const incoming = value ?? ''
    if (current === incoming) return

    editor.commands.setContent(incoming || '', { emitUpdate: false })
  }, [editor, value, variant])

  const applyLink = () => {
    if (!editor) return
    const trimmed = linkUrl.trim()
    if (!isSafeHttpUrl(trimmed)) {
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run()
    setLinkOpen(false)
  }

  const shellClass = focused ? `${styles.shell} ${styles.shellFocused}` : styles.shell
  const surfaceClass =
    variant === 'inline' ? `${styles.surface} ${styles.surfaceInline}` : styles.surface

  return (
    <div
      className={shellClass}
      style={{
        ['--campaign-rich-min-height' as string]: `${minHeight ?? (variant === 'inline' ? 44 : 120)}px`,
      }}
    >
      <div className={styles.toolbar} role="toolbar" aria-label="Formatação do texto">
        <button
          type="button"
          className={
            editor?.isActive('bold')
              ? `${styles.toolButton} ${styles.toolButtonActive}`
              : styles.toolButton
          }
          aria-label="Negrito"
          aria-pressed={editor?.isActive('bold') ?? false}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Icon icon="mdi:format-bold" />
        </button>
        <button
          type="button"
          className={
            editor?.isActive('italic')
              ? `${styles.toolButton} ${styles.toolButtonActive}`
              : styles.toolButton
          }
          aria-label="Itálico"
          aria-pressed={editor?.isActive('italic') ?? false}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Icon icon="mdi:format-italic" />
        </button>
        {variant === 'block' ? (
          <>
            <button
              type="button"
              className={
                editor?.isActive('bulletList')
                  ? `${styles.toolButton} ${styles.toolButtonActive}`
                  : styles.toolButton
              }
              aria-label="Lista"
              aria-pressed={editor?.isActive('bulletList') ?? false}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
            >
              <Icon icon="mdi:format-list-bulleted" />
            </button>
            <button
              type="button"
              className={
                editor?.isActive('orderedList')
                  ? `${styles.toolButton} ${styles.toolButtonActive}`
                  : styles.toolButton
              }
              aria-label="Lista numerada"
              aria-pressed={editor?.isActive('orderedList') ?? false}
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            >
              <Icon icon="mdi:format-list-numbered" />
            </button>
            <button
              type="button"
              className={
                editor?.isActive('link')
                  ? `${styles.toolButton} ${styles.toolButtonActive}`
                  : styles.toolButton
              }
              aria-label="Link"
              aria-pressed={linkOpen || (editor?.isActive('link') ?? false)}
              onClick={() => {
                setLinkUrl(editor?.getAttributes('link').href || 'https://')
                setLinkOpen((current) => !current)
              }}
            >
              <Icon icon="mdi:link-variant" />
            </button>
          </>
        ) : null}
      </div>
      {variant === 'block' && linkOpen ? (
        <div className={styles.linkBar}>
          <input
            className={styles.linkInput}
            value={linkUrl}
            onChange={(event) => setLinkUrl(event.target.value)}
            placeholder="https://exemplo.com"
            aria-label="Endereço do link"
          />
          <button type="button" className={styles.linkApply} onClick={applyLink}>
            Aplicar
          </button>
          <button
            type="button"
            className={styles.linkCancel}
            onClick={() => setLinkOpen(false)}
          >
            Cancelar
          </button>
        </div>
      ) : null}
      <div className={surfaceClass}>
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
